import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import "react-datepicker/dist/react-datepicker.css";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";

import {
  approveRoster,
  getEmployeeRoster,
  resyncEmployeeRoster,
  updateProgressiveOnboarding,
} from "actions/employerActions";
import { employerConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import IconSpinner from "components/IconSpinner";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import { Card, Form } from "react-bootstrap";
import { isEmpty, keys, map, size } from "lodash";
import Paginator from "components/Paginator";
import Button from "components/Button";
import StatusBox, { statusBoxStatuses } from "components/StatusBox";
import { FaUndo } from "react-icons/fa";
import { toast } from "react-toastify";
import type { RootState } from "types/state";

const linkStatuses = {
  UNKNOWN: "UNKNOWN",
  PENDING: "PENDING",
  ACTIVE: "ACTIVE",
  REJECTED: "REJECTED",
  ADMIN_REVIEW_REQUIRED: "ADMIN_REVIEW_REQUIRED",
  DEACTIVATED_BY_EMPLOYER: "DEACTIVATED_BY_EMPLOYER",
};

function linkStatusToStatusBox(linkStatus: string | undefined) {
  let status, type;
  switch (linkStatus) {
    case linkStatuses.UNKNOWN: {
      status = "NEW";
      type = statusBoxStatuses.primary;
      break;
    }
    case linkStatuses.PENDING: {
      status = "PENDING";
      type = statusBoxStatuses.success;
      break;
    }
    case linkStatuses.ACTIVE: {
      status = "ACTIVE";
      type = statusBoxStatuses.success;
      break;
    }
    case linkStatuses.REJECTED: {
      status = "REJECTED";
      type = statusBoxStatuses.danger;
      break;
    }
    case linkStatuses.DEACTIVATED_BY_EMPLOYER: {
      status = "DEACTIVATED";
      type = statusBoxStatuses.danger;
      break;
    }
    default: {
      status = "NEW";
      type = statusBoxStatuses.primary;
      break;
    }
  }

  return <StatusBox status={status} type={type} />;
}

const isApproving = createLoadingSelector([
  employerConstants.UPDATE_PROGRESSIVE_ONBOARDING,
  employerConstants.APPROVE_ROSTER,
]);
const isResyncing = createLoadingSelector(employerConstants.RESYNC_ROSTER);

const isGettingRoster = createLoadingSelector(
  employerConstants.GET_EMPLOYEE_ROSTER
);

const error = createErrorSelector([
  employerConstants.UPDATE_PROGRESSIVE_ONBOARDING,
  employerConstants.RESYNC_ROSTER,
  employerConstants.APPROVE_ROSTER,
]);

const mapStateToProps = (state: RootState) => {
  return {
    isApproving: isApproving(state),
    isResyncing: isResyncing(state),
    error: error(state),
    employeeRoster: state.employer.paginatedEmployeeRoster,
    employeeRosterCount: state.employer.paginatedEmployeeRosterCount,
    // employeeRosterFilterValues is `object` in the employer slice — narrow at
    // the boundary to the shape this component reads (`.state` is mapped over).
    employeeRosterFilterValues: state.employer
      .employeeRosterFilterValues as EmployeeRosterFilterValues,
    isGettingRoster: isGettingRoster(state),
  };
};

const mapDispatchToProps = {
  updateProgressiveOnboarding,
  getEmployeeRoster,
  resyncEmployeeRoster,
  approveRoster,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose: () => void;
  isFullyOnboarded?: boolean;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface EmployeeRosterFilterValues {
  state?: Array<string | null> | null;
  city?: Array<string | null> | null;
}

/** Row shape the IconTable columns read off each employee roster entry. */
interface RosterRow extends Record<string, unknown> {
  employeeId?: string;
  linkStatus?: string;
}

interface State {
  initialFetching: boolean;
  limit: number;
  offset: number;
  page: number;
  selectedEmployees: Record<string, boolean>;
  rejectedEmployees: Record<string, boolean>;
  // read in _getPaginatedRoster / the filter <select>, only ever written via setState
  stateFilter?: string;
}

class RosterApprovalTable extends React.PureComponent<Props, State> {
  static defaultProps = {
    isFullyOnboarded: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
      limit: 50,
      offset: 0,
      page: 0,
      selectedEmployees: {},
      rejectedEmployees: {},
    };
  }

  async componentDidMount() {
    await this._getPaginatedRoster();
    this.setState({
      initialFetching: false,
    });
  }

  _getPaginatedRoster = () => {
    const filter =
      this.state.stateFilter && this.state.stateFilter !== "all"
        ? `state|eq|${this.state.stateFilter}`
        : "";

    const params = {
      limit: this.state.limit,
      offset: this.state.offset,
      filter,
    };
    return this.props.getEmployeeRoster(
      this.props.client as ApolloClient<object>,
      params
    );
  };

  _approveRoster = () => {
    const rejectedEmployeeIds = keys(this.state.rejectedEmployees);
    void this.props
      .approveRoster(
        this.props.client as ApolloClient<object>,
        rejectedEmployeeIds
      )
      .then(() => {
        if (this.props.error) {
          toast.error("Something went wrong during roster approval.");
          return;
        }
        toast.success("Successfully approved roster.");
        this.props.onClose();
      });
  };

  _resyncRoster = async () => {
    await this.props.resyncEmployeeRoster(
      this.props.client as ApolloClient<object>
    );
    this.props.onClose();
  };

  _handleReject = () => {
    this.setState({
      selectedEmployees: {},
      rejectedEmployees: {
        ...this.state.selectedEmployees,
        ...this.state.rejectedEmployees,
      },
    });
  };

  _removeFromRejected = (employeeId: string) => {
    const rejectedEmployees = { ...this.state.rejectedEmployees };

    delete rejectedEmployees[employeeId];
    this.setState({
      rejectedEmployees,
    });
  };

  _getContent = () => {
    const tableData = map(this.props.employeeRoster as RosterRow[], (emp) => ({
      ...emp,
      unselectable:
        emp.linkStatus !== linkStatuses.UNKNOWN ||
        this.state.rejectedEmployees[emp.employeeId!],
    }));
    const rosterColumns: Array<IconTableColumn<RosterRow>> = [
      {
        key: "firstName",
        label: "First Name",
      },
      {
        key: "lastName",
        label: "Last Name",
      },
      {
        key: "dob",
        label: "DOB",
      },
      {
        key: "email",
        label: "Email",
      },
      {
        label: "status",
        customComponent: ({ linkStatus, employeeId }) => {
          if (this.state.rejectedEmployees[employeeId!]) {
            return linkStatusToStatusBox(linkStatuses.REJECTED);
          }
          return linkStatusToStatusBox(linkStatus);
        },
      },
      {
        customComponent: ({ employeeId }) => {
          if (this.state.rejectedEmployees[employeeId!]) {
            return (
              <div className="undo-button">
                <FaUndo
                  size={16}
                  onClick={() => this._removeFromRejected(employeeId!)}
                />
              </div>
            );
          }

          return null;
        },
      },
    ];
    return (
      <>
        <div className="filters-and-buttons">
          <div className="table-filters">
            <span>
              <Form.Select
                className="table-filter"
                id="inlineFormCustomSelect"
                value={this.state.stateFilter}
                onChange={(e) => {
                  this.setState(
                    {
                      stateFilter: e.target.value,
                    },
                    () => {
                      void this._getPaginatedRoster();
                    }
                  );
                }}
              >
                <option value="all">All States</option>
                {map(this.props.employeeRosterFilterValues.state, (state) => (
                  <option id={state!} value={state!} key={state!}>
                    {state}
                  </option>
                ))}
              </Form.Select>
            </span>
          </div>

          <div className="reject-btn-container">
            <Button
              disabled={isEmpty(this.state.selectedEmployees)}
              color={"red"}
              name="action"
              onClick={this._handleReject}
            >
              Reject Selected
            </Button>
          </div>
        </div>
        {this.props.isGettingRoster && <IconSpinner centered />}
        <IconTable
          columns={rosterColumns}
          data={tableData}
          hasSelectableRows
          uniqueRowIdentifier="employeeId"
          selectedRows={this.state.selectedEmployees}
          onRowSelect={(id, checked) => {
            const selectedEmployees = { ...this.state.selectedEmployees };

            if (!checked) {
              delete selectedEmployees[id];
            } else {
              selectedEmployees[id] = checked;
            }
            this.setState({
              selectedEmployees,
            });
          }}
        />
        <Paginator
          onChange={this._onPageChange}
          pageCount={this.props.employeeRosterCount / this.state.limit}
          page={this.state.page}
        />
        <div className="submit-row">
          <span className="cancel-btn">
            <Button
              btnLabel="Re-Sync Roster"
              color="cancel"
              name="cancel"
              type="button"
              withArrow={true}
              loading={this.props.isResyncing}
              onClick={this._resyncRoster}
            />
          </span>
          <Button
            btnLabel="Approve Roster"
            name="submit"
            withArrow={true}
            loading={this.props.isApproving}
            onClick={this._approveRoster}
          />
        </div>
      </>
    );
  };

  _onPageChange = ({ selected }: { selected: number }) => {
    const offset = selected * this.state.limit;

    this.setState({ page: selected, offset }, () => this._getPaginatedRoster());
  };

  render() {
    const rosterSize = size(this.props.employeeRoster);

    return (
      <Card className={"table-container"}>
        <div className="widget-header">
          <span>
            <Card.Title>
              Employee Roster:
              <p className="number-context">
                Pending (<p className="number">{rosterSize}</p>)
              </p>
            </Card.Title>
          </span>
        </div>
        <Card.Body>
          {this.state.initialFetching && <IconSpinner centered />}
          {!this.state.initialFetching && this._getContent()}
        </Card.Body>
      </Card>
    );
  }
}

export default connector(withApollo<Props>(RosterApprovalTable));
